import React, { useLayoutEffect, useRef } from 'react';
import { Button, Text } from '@fragments';
import { IconInfinity, IconTrendingUp, IconX } from '@tabler/icons-react';
import './styles.scss';

// The trending topic chosen for this creation, inside the prompt as a chip: it sits at
// the start of the first line and the text flows after it (the field's first line is
// indented by the chip's width, `--studio-token-indent` on the field's wrapper, the chip's
// parent). It scrolls with the text. Its ✕, or Backspace at the very start of the field,
// removes it; the topics come back above the prompt.
export default function TopicToken({ t, topic, onRemove }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    // The wrapper is read from the DOM, not from its ref: when both mount together (a
    // reload, or back home with a topic chosen) this effect runs before the ref is set.
    const token = ref.current;
    const host = token?.parentElement;
    const field = host?.querySelector('textarea');
    if (!field) return undefined;
    // From where the field's text starts to the chip's end, plus the chip's own margin.
    const measure = () => {
      const start = field.getBoundingClientRect().left + parseFloat(getComputedStyle(field).paddingLeft);
      const end = token.getBoundingClientRect().right + parseFloat(getComputedStyle(token).marginRight);
      host.style.setProperty('--studio-token-indent', `${Math.max(0, Math.ceil(end - start))}px`);
    };
    const follow = () => host.style.setProperty('--studio-token-shift', `${-field.scrollTop}px`);
    measure();
    follow();
    const observer = new ResizeObserver(measure);
    observer.observe(token);
    field.addEventListener('scroll', follow, { passive: true });
    return () => {
      observer.disconnect();
      field.removeEventListener('scroll', follow);
      host.style.removeProperty('--studio-token-indent');
      host.style.removeProperty('--studio-token-shift');
    };
  }, [topic.id]);

  const trending = topic.classification === 'trending';
  return <span ref={ref} className="studio-topic-token" role="group" aria-label={t('topicTokenLabel', { title: topic.title })} title={topic.title}>
    <Text.caption className="studio-topic-token__title" weight={500} contrast={9} left={trending ? <IconTrendingUp /> : <IconInfinity />}>{topic.title}</Text.caption>
    <Button className="studio-topic-token__remove" variant="text" color="overlay" size="micro" preventLoading icon={<IconX />}
      aria-label={t('topicTokenRemove')} tooltip={t('topicTokenRemove')} onClick={onRemove} />
  </span>;
}
